/**
 * Help — what the figures mean, and what is currently limiting them.
 *
 * Deliberately not a written manual. Static help prose goes stale silently: it
 * is true when written, the code moves, and nothing forces the page to notice.
 * This product spent a long time carrying figures that were true once and wrong
 * later, and a help centre is the same failure mode in paragraph form.
 *
 * So everything here is either read live from the workspace or printed from the
 * definitions the menu's pages use. The limits are /overview box 8's list, from
 * the same strict fetchMMMStatus read; every threshold comes from the constant
 * its page reads, and every word quoted from a page goes through that page's
 * own key (helpContent, helpBudget). Nothing is retyped here.
 *
 * @module pages/Help
 */

import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Loader2, Info } from 'lucide-react';
import { YDMPageHero } from '@/components/brand';
import { DataQualityBox } from '@/components/looker/app/DataQualityBox';
import { PlatformFieldMap } from '@/components/help/PlatformFieldMap';
import { SectionCard, TermGrid } from '@/components/help/HelpCards';
import { definitionsOf, pageRulesOf, resultsOf, rulesOf, sharedPagesOf } from '@/components/help/helpContent';
import { budgetOf } from '@/components/help/helpBudget';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { fetchMMMStatus } from '@/services/mediaIntelligenceService';
import type { MMMStatus } from '@/services/mediaIntelligenceService';
import { logError } from '@/services/logger';
import { DASH } from '@/services/overview/overviewFormat';
import { isHelpAnchor, type HelpAnchor } from '@/components/help/metricHelpRegistry';

/** The cards in page order; a card's number is its place here. Labels are thunks: t is the reader's, at render. */
const NAV: ReadonlyArray<{ id: HelpAnchor; label: (t: TFunction) => string }> = [
  { id: "limits", label: (t) => t('help.nav.limits') },
  { id: "coverage", label: (t) => t('help.nav.coverage') },
  { id: "rules", label: (t) => t('help.nav.rules') },
  { id: "results", label: (t) => t('help.nav.results') },
  { id: "definitions", label: (t) => t('help.nav.definitions') },
  { id: "budget", label: (t) => t('nav.budgetMmm') },
  { id: "platforms", label: (t) => t('help.nav.platforms') },
];

const numberOf = (id: HelpAnchor): number => NAV.findIndex((nav) => nav.id === id) + 1;

const Help: React.FC = () => {
  const { t } = useTranslation();
  const { workspace } = useWorkspace();
  const [status, setStatus] = useState<MMMStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const { hash } = useLocation();
  const [target, setTarget] = useState<HelpAnchor | null>(null);
  /** The last arrival this effect scrolled to, and where the page stood after it. */
  const arrival = useRef<{ hash: string; y: number } | null>(null);

  // BrowserRouter (main.tsx:15) does not scroll to a hash after a <Link>, and
  // :target never matches after pushState, so arriving from a "?" is done here.
  // `loading` re-runs it once the live sections above have grown, unless the
  // reader has scrolled since. Only HELP_ANCHORS ids: Layout's skip link
  // (#main-content) is not ours. The NAV links below change the hash too, so a
  // NAV click gets the same focus and ring, on purpose.
  useEffect(() => {
    const id = hash.slice(1);
    if (!isHelpAnchor(id)) {
      // Forget the last arrival too, or coming back to that same anchor later
      // (skip link out, read, then back) looks like this effect's own re-run and
      // the scrolled-away guard below swallows a deliberate arrival.
      arrival.current = null;
      setTarget(null);
      return;
    }
    const el = document.getElementById(id);
    if (!el) return;
    const prev = arrival.current;
    if (prev && prev.hash === hash && Math.abs(window.scrollY - prev.y) > 1) return;
    el.scrollIntoView?.({ block: "start" }); // jsdom 29 has no scrollIntoView at all
    el.focus({ preventScroll: true });
    arrival.current = { hash, y: window.scrollY };
    setTarget(id);
  }, [hash, loading]);

  // `t` is a dependency on purpose: fetchMMMStatus bakes the reader's language
  // into its rows, so a language switch has to rebuild them. Strict, as box 8
  // reads it: a swallowed probe failure would read as "nothing to fix".
  useEffect(() => {
    if (!workspace) return;
    let ignore = false;
    fetchMMMStatus({ workspaceIds: [workspace.id] }, t, { strict: true })
      .then((s) => { if (!ignore) setStatus(s); })
      .catch((e) => {
        logError('Help', 'Could not load workspace status', e);
        if (!ignore) setStatus(null);
      })
      .finally(() => { if (!ignore) setLoading(false); });
    return () => { ignore = true; };
  }, [workspace?.id, t]);

  const coverage =
    status && status.spanDays > 0 ? Math.round((status.dataDays / status.spanDays) * 100) : null;
  const card = (id: HelpAnchor) => ({ id, n: numberOf(id), targeted: target === id });
  const failed = <p className="rounded-lg border border-neutral-200 bg-white p-4 text-sm text-neutral-700">{t('help.readFailed')}</p>;

  return (
    <div className="space-y-4 animate-fade-in">
      {/* The headline is the question this page answers — the slogan told a
          reader nothing about what they had opened (user decision 2026-09-02). */}
      <YDMPageHero eyebrow={t("help.eyebrow")} headline={t("help.headline")} subtitle={t("help.subtitle")} />

      {/* Reference pages get long; the nav keeps every section one tap away. */}
      <nav className="sticky top-0 z-10 -mx-1 flex flex-wrap gap-2 bg-neutral-50/95 px-1 py-2 backdrop-blur-sm">
        {NAV.map((nav, i) => (
          <a
            key={nav.id}
            href={`#${nav.id}`}
            className="flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-xs font-semibold text-ink-900 hover:border-ink-900"
          >
            <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-ink-950 text-[10px] font-extrabold text-white">
              {i + 1}
            </span>
            {nav.label(t)}
          </a>
        ))}
      </nav>

      {/* Live, per workspace: /overview box 8's list from the same read, so the two
          pages cannot give different answers about the same account. */}
      <SectionCard {...card("limits")} title={t("help.limits.title")}
        hint={t("help.limits.hint", { box: t("overview.box.quality"), page: t("nav.executiveOverview") })}>
        {loading ? (
          <div className="flex items-center gap-2 rounded-lg border border-neutral-200 bg-white p-4 text-sm text-neutral-700">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            {t("looker.app.loading")}
          </div>
        ) : status ? <DataQualityBox actions={status.dataQuality} /> : failed}
      </SectionCard>

      <SectionCard {...card("coverage")} title={t("help.coverage.title")} hint={t("help.coverage.hint")}>
        {loading ? (
          <span className="text-sm text-neutral-700">{t("looker.app.loading")}</span>
        ) : status ? (
          <div className="flex items-center gap-4">
            <span className="text-2xl font-extrabold text-ink-900 tabular-nums">
              {status.dataDays.toLocaleString()}
            </span>
            <span className="text-xs text-neutral-700 whitespace-nowrap tabular-nums">
              {t("help.coverage.of", { span: status.spanDays.toLocaleString() })}
            </span>
            <span className="h-2.5 flex-1 overflow-hidden rounded-full bg-neutral-100">
              <span
                className="block h-full rounded-full bg-success-600"
                style={{ width: `${coverage ?? 0}%` }}
              />
            </span>
            {coverage !== null && (
              <span className="text-sm font-extrabold text-success-700 tabular-nums">{coverage}%</span>
            )}
          </div>
        ) : failed}
      </SectionCard>

      <SectionCard {...card("rules")} title={t("help.rules.title")} hint={t("help.rules.hint", { pages: sharedPagesOf(t) })}>
        <TermGrid terms={rulesOf(t)} target={target} />
      </SectionCard>

      <SectionCard {...card("results")} title={t("help.results.title")} hint={t("help.results.hint")}>
        <TermGrid terms={resultsOf(t)} target={target} />
      </SectionCard>

      <SectionCard {...card("definitions")} title={t("help.def.title")} hint={t("help.def.hint")}>
        <TermGrid terms={definitionsOf(t)} target={target} />
        <h3 className="mt-4 text-xs font-extrabold text-ink-900">{t("help.def.pages")}</h3>
        {pageRulesOf(t).map((p) => (
          <div key={p.page} className="mt-2">
            <p className="text-xs font-semibold text-ink-900">{p.page}</p>
            <ul className="mt-1 space-y-1 text-xs text-neutral-700">
              {p.notes.map((note) => <li key={note}>• {note}</li>)}
            </ul>
          </div>
        ))}
      </SectionCard>

      <SectionCard {...card("budget")} title={t("help.budget.title", { page: t("nav.budgetMmm") })} hint={t("help.budget.hint")}>
        <TermGrid terms={budgetOf(t)} target={target} />
      </SectionCard>

      {/* The one table here that is written down rather than read live. It cannot
          be derived: the five clients build PlatformMetric imperatively. So it is
          bound to them by fieldMapping.test.ts instead — see the module header. */}
      <SectionCard {...card("platforms")} title={t("help.platforms.title")} hint={t("help.platforms.hint")}>
        <PlatformFieldMap />
        <p className="mt-3 text-xs text-neutral-700">
          {t("help.platforms.note", {
            engagement: t("creatives.col.engagement"), creatives: t("nav.creativePerformance"),
            engagements: t("help.metricEngagements"), notReported: t("overview.notReported"), dash: DASH,
          })}
        </p>
      </SectionCard>

      <p className="flex items-start gap-1.5 text-xs text-neutral-700">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <span>{t("help.footer")}</span>
      </p>
    </div>
  );
};

export default Help;
